import { Injectable } from '@angular/core';
import mime from 'mime';
import { dsLogger } from '../../shared/services/logger/logger.instance';
import type { PalantirDialogRef } from '../dialog/dialog-ref.service';
import { PalantirDialogService } from '../dialog/dialog.service';
import { PalantirCarouselComponent } from './carousel.component';
import type {
  CarouselSlide,
  CarouselParams,
  CarouselTexts,
  CarouselMediaErrors,
} from './carousel.types';

const logger = dsLogger.extend('carousel');

@Injectable({ providedIn: 'root' })
export class PalantirCarouselService {
  public texts?: CarouselTexts;

  public get slides(): CarouselSlide[] {
    return this._slides;
  }

  public set slides(slides: CarouselSlide[]) {
    this._slides = this.formatSlides(slides);
    this.preloadImages();
  }

  private _slides: CarouselSlide[] = [];

  private mediaErrors: CarouselMediaErrors = {};

  private initialized = false;

  public constructor(private readonly palantirDialogService: PalantirDialogService) {}

  public initialize(params: CarouselParams): void {
    this.texts = params.texts;
    this.slides = params.slides;

    if (this.slides.length) {
      this.initialized = true;
    }
  }

  public open(index: number = 0): PalantirDialogRef {
    if (!this.initialized) {
      throw new Error('Carousel not initialized');
    }

    return this.palantirDialogService.openDialog(PalantirCarouselComponent, {
      size: 'large',
      data: {
        slides: this.slides,
        texts: this.texts,
        mediaErrors: this.mediaErrors,
        openAt: index,
      },
    });
  }

  public handleMediaError(event: any, index: number) {
    event.target.removeAttribute('src');
    this.mediaErrors[index] = true;
  }

  private preloadImages() {
    for (const slide of this.slides) {
      const media = slide.format === 'image' ? new Image() : document.createElement('video');
      media.src = slide.src;
      media.onerror = (err) => {
        logger.error(err);
        this.handleMediaError({ target: media }, this.slides.indexOf(slide));
      };
    }
  }

  private formatSlides(slides: CarouselSlide[]): CarouselSlide[] {
    return slides.map((slide) => {
      if (slide.format) {
        return slide;
      }

      const mimeType = mime.getType(slide.src);

      if (!mimeType) {
        logger.error('Invalid mime type for carousel slide', slide.src);
        throw new Error('Invalid mime type for carousel slide');
      }

      let format: CarouselSlide['format'];

      if (mimeType.includes('image')) {
        format = 'image';
      }

      if (mimeType.includes('video')) {
        format = 'video';
      }

      return {
        src: slide.src,
        format,
      };
    });
  }
}
